import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ZoomInIcon } from 'lucide-react';
import { Lightbox } from '../Lightbox/Lightbox';
import { PROJECT_NAME, PROJECT_PAGES } from '../../data/woodworking';
import { useSlideTrack } from '../../hooks/useSlideTrack';
import { preloadImages } from '../../utils/preload';
const arrowClass = 'flex h-11 w-11 items-center justify-center rounded-full border border-ink/20 text-ink transition-colors duration-150 ease-out hover:border-forest hover:text-forest disabled:opacity-30 disabled:hover:border-ink/20 disabled:hover:text-ink focus:outline-none focus-visible:ring-4 focus-visible:ring-forest/40';

/**
 * All eight real pages of Project 04 (Volume 01). One full page per view,
 * never auto-advancing; every page opens in a zoomable viewer.
 */
export function ProjectPreviewSection() {
  const total = PROJECT_PAGES.length;
  const {
    trackRef,
    active,
    goTo
  } = useSlideTrack(total);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const current = PROJECT_PAGES[active];
  const sectionRef = useRef<HTMLElement>(null);
  const [warm, setWarm] = useState(false);

  // Download all eight pages once the section approaches the viewport, so
  // changing page never shows an empty frame while an image loads.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const start = () => {
      setWarm(true);
      preloadImages(PROJECT_PAGES.map(page => page.src));
    };
    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        start();
        observer.disconnect();
      }
    }, {
      rootMargin: '800px 0px'
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, []);
  const navigateLightbox = (index: number) => {
    setLightboxIndex(index);
    goTo(index, true);
  };
  const onTrackKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(active + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(active - 1);
    }
  };
  return <section ref={sectionRef} id="project-preview" aria-labelledby="preview-title" className="bg-cream py-14 text-ink sm:py-20 lg:py-24" data-id="element-227" data-mp="2dvlzxiyllfa2">
      <div className="mx-auto grid w-full max-w-6xl px-5 sm:px-8 lg:grid-cols-12 lg:gap-x-14" data-id="element-228" data-mp="31mrezvwps3jb">
        <header className="lg:col-span-5 lg:col-start-1 lg:row-start-1 lg:self-end" data-id="element-229" data-mp="35m08cznudzec">
          <p className="label-tech flex items-center gap-3 text-forest" data-id="element-230" data-mp="2ro1tlt5m00yb">
            <span className="h-px w-6 shrink-0 bg-current" aria-hidden data-id="element-231" data-mp="2wmd7qg2xsxmc" />
            Real pages · Volume 01
          </p>
          <h2 id="preview-title" className="mt-4 text-balance font-brand text-[1.8rem] font-bold leading-[1.18] tracking-[-0.01em] text-ink min-[390px]:text-[1.95rem] sm:text-[2.45rem] lg:text-[2.6rem]" data-id="element-232" data-mp="1g7u0sjfzc3zu">
            See one complete project before you buy
          </h2>
          <p className="mt-4 font-brand text-[1.12rem] font-bold leading-snug text-ember-deep sm:text-[1.25rem]" data-id="element-233" data-mp="18pdspbnny2lj">
            {PROJECT_NAME}
          </p>
          <p className="mt-3 max-w-xl text-[1rem] leading-relaxed text-stone" data-id="element-234" data-mp="1mlzjk2p42r6g">
            Explore all eight actual pages from Volume 01—from dimensions and cut lists to joinery, drawer hardware,
            assembly, and final checks.
          </p>
        </header>

        <div className="mt-8 lg:col-span-7 lg:col-start-6 lg:row-span-2 lg:row-start-1 lg:mt-0" data-id="element-235" data-mp="pznvkhwfh4qg">
          <div className="relative mx-auto w-full max-w-[480px] lg:max-w-[calc(min(80svh,820px)*0.773)]" data-id="element-236" data-mp="13r2fcqy5t9yk">
            <ul ref={trackRef} tabIndex={0} aria-label={`${PROJECT_NAME} pages, ${total} total. Use arrow keys to change page.`} onKeyDown={onTrackKeyDown} className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto rounded-[6px] bg-dune shadow-[0_40px_70px_-34px_rgba(31,32,34,0.55)] ring-1 ring-ink/10 focus:outline-none focus-visible:ring-4 focus-visible:ring-forest/50" data-id="element-237" data-mp="yx7wfpqdoz0j">
              {PROJECT_PAGES.map((page, index) => <li key={page.src} className="w-full shrink-0 snap-center" aria-label={`Page ${index + 1} of ${total}`} data-id="element-238" data-mp="tt5rnv6igo5b" data-mpi={index}>
                  <button type="button" onClick={() => setLightboxIndex(index)} tabIndex={index === active ? 0 : -1} aria-label={`Enlarge page ${index + 1} of ${total}: ${page.caption}`} className="block aspect-[1688/2184] w-full cursor-zoom-in focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-olive-light" data-id="element-239" data-mp="12nkfwn90r90k">
                    <img src={page.src} alt={page.alt} width={page.width} height={page.height} loading={index === 0 || warm ? 'eager' : 'lazy'} decoding="async" draggable={false} className="h-full w-full object-contain" data-id="element-240" data-mp="3eepgcbb737au" />
                  </button>
                </li>)}
            </ul>
            <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-charcoal/90 px-3 py-1.5 text-[0.82rem] font-semibold text-white ring-1 ring-white/15" data-id="element-241" data-mp="1mr9knv2ibejv">
              <ZoomInIcon className="h-4 w-4 text-olive-light" aria-hidden data-id="element-242" data-mp="2fbzod9gbtkfn" />
              Tap to enlarge
            </span>
          </div>
        </div>

        <div className="mx-auto mt-5 w-full max-w-[480px] lg:col-span-5 lg:col-start-1 lg:row-start-2 lg:mx-0 lg:mt-10 lg:max-w-none lg:self-start" data-id="element-243" data-mp="25ev1t8ikjmyb">
          <div className="flex items-center justify-between gap-4" data-id="element-244" data-mp="yxhoorvm0yfq">
            <div className="min-w-0" aria-live="polite" data-id="element-245" data-mp="tz6euh5benxi">
              <p className="label-tech text-forest" data-id="element-246" data-mp="vheu4uoa7z8r">
                Page {active + 1} of {total}
              </p>
              <p className="mt-1 font-brand text-[1.1rem] font-bold leading-snug text-ink" data-id="element-247" data-mp="10h9hio8um6p2">{current.caption}</p>
            </div>
            <div className="flex shrink-0 gap-2" data-id="element-248" data-mp="mgqi25ig88yi">
              <button type="button" onClick={() => goTo(active - 1)} disabled={active === 0} aria-label="Previous page" className={arrowClass} data-id="element-249" data-mp="rftljlis674d">
                <ChevronLeftIcon className="h-5 w-5" aria-hidden data-id="element-250" data-mp="hjq83jiham24" />
              </button>
              <button type="button" onClick={() => goTo(active + 1)} disabled={active === total - 1} aria-label="Next page" className={arrowClass} data-id="element-251" data-mp="3s1x5jjyu3mq">
                <ChevronRightIcon className="h-5 w-5" aria-hidden data-id="element-252" data-mp="3q0bc0lw96oyd" />
              </button>
            </div>
          </div>

          <ol className="mt-4 grid grid-cols-4 gap-2 sm:grid-cols-8" aria-label="Pages" data-id="element-253" data-mp="3baqdstibxj2h">
            {PROJECT_PAGES.map((page, index) => <li key={page.src} data-id="element-254" data-mp="36dq6ygdnocab" data-mpi={index}>
                <button type="button" onClick={() => goTo(index)} aria-label={`Show page ${index + 1} of ${total}: ${page.caption}`} aria-current={index === active ? 'true' : undefined} className={`block aspect-[1688/2184] w-full overflow-hidden rounded-[3px] bg-dune transition-[box-shadow,opacity] duration-150 ease-out focus:outline-none focus-visible:ring-4 focus-visible:ring-forest/50 ${index === active ? 'opacity-100 ring-2 ring-forest' : 'opacity-60 ring-1 ring-ink/15 hover:opacity-90'}`} data-id="element-255" data-mp="36ejehjkfv5ti">
                  <img src={page.src} alt="" width={page.width} height={page.height} loading="lazy" decoding="async" className="h-full w-full object-contain" data-id="element-256" data-mp="gw9a4h7v3t33" />
                </button>
              </li>)}
          </ol>
        </div>
      </div>

      <Lightbox items={PROJECT_PAGES} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={navigateLightbox} label={PROJECT_NAME} counterNoun="Page" data-id="element-257" data-mp="1t79qm773jm42" />
    </section>;
}